import { cn } from '@/lib/utils';

/**
 * Loading placeholder.
 *
 * The fill is `--color-skeleton-fill` rather than a literal so each surface can
 * set a value that is actually legible on the page it renders on. The default
 * (globals.css) is the neutral this used to hardcode, which is correct on the
 * white admin/workspace cards; the client shell overrides it for the client
 * wash, where that neutral composites to a 14/10/10 RGB step and disappears.
 *
 * IT SHOWS IMMEDIATELY, and that is a deliberate reversal.
 *
 * There was briefly a 220ms reveal delay here, on the reasoning that client
 * navigations resolve in ~110ms and a placeholder that appears and vanishes
 * inside that window reads as a flicker. That reasoning was right about the
 * flicker and wrong about the cause: the placeholders at the time were single
 * flat blocks the size of a whole card (`<Skeleton className="h-94 w-full" />`
 * and friends), so what flashed was a grey slab dropping onto the page and
 * lifting off again. Hiding it was treating the symptom.
 *
 * With the real fix in place — see components/client-ui/skeletons.tsx, where a
 * skeleton is now the CARD with placeholder shapes inside it, not a block
 * standing in for the card — the same 110ms reads as the card filling in.
 * Nothing changes shape, so there is nothing to flicker. Showing it is now
 * strictly better than hiding it, and hiding it would just mean a blank hole
 * where a card is about to be.
 *
 * If you ever need the gate back, it was `opacity-0` plus
 * `[animation-delay:Nms]` and `motion-reduce:opacity-100` — no timer, no state,
 * and it worked (measured peak opacity 0.000 on fast loads). But do check the
 * skeleton's SHAPE first: a delay is the right tool only when the placeholder
 * itself is the problem.
 */
function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('bg-skeleton-fill animate-pulse rounded-md', className)} {...props} />;
}

export { Skeleton };
